/* Master 30-Day Book — combines EVERY active flipbook into one print-ready
   document.  Used by the backend /api/flipbooks/master-book/pdf endpoint
   with headless Chrome.  Same A5 page size, same look as single-flipbook print. */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { RENDER, TextP } from "./FlipbookPrint";

const PAGE_W = 560;
const PAGE_H = 794;

const pageBoxStyle = {
  width: `${PAGE_W}px`,
  height: `${PAGE_H}px`,
  background: "#FAF9F6",
  position: "relative",
  overflow: "hidden",
  pageBreakAfter: "always",
  breakAfter: "page",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
};

/* Master cover for the whole book */
function MasterCover({ count }) {
  return (
    <div style={{ ...pageBoxStyle, padding: 0 }}>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(160deg, #0F172A 0%, #0F172A 50%, #2E9F3A 100%)",
        }}/>
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "radial-gradient(ellipse at 30% 20%, rgba(201,162,39,0.28), transparent 55%), radial-gradient(ellipse at 70% 85%, rgba(255,255,255,0.08), transparent 60%)",
        }}/>
        <div style={{
          position: "absolute", left: 36, right: 36, top: "50%",
          transform: "translateY(-50%)", color: "#FAF9F6", textAlign: "center",
        }}>
          <div style={{ fontSize: 11, letterSpacing: "0.42em", color: "#F39A1F", fontWeight: 700, marginBottom: 26 }}>
            ★ FAITHARAMA · 30 DAY CHALLENGE ★
          </div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 52, lineHeight: 1.05, margin: "0 0 18px",
            fontWeight: 700, textTransform: "uppercase",
          }}>
            With Faith<br/>All Things<br/>Are Possible
          </h1>
          <div style={{
            width: 140, height: 3, background: "#F39A1F",
            margin: "22px auto 22px",
          }}/>
          <p style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 18, fontStyle: "italic", margin: "0 auto", maxWidth: 360, opacity: 0.92,
          }}>
            A {count}-Day Interactive Companion · Lessons, Reflections & Quizzes
          </p>
          <div style={{ marginTop: 46, fontSize: 11, letterSpacing: "0.32em", fontWeight: 700, color: "#F39A1F" }}>
            BY MIKE BOYD · FAITHARAMA.COM
          </div>
        </div>
      </div>
    </div>
  );
}

/* Day separator before each flipbook */
function DaySeparator({ fb }) {
  return (
    <div style={pageBoxStyle}>
      <div style={{
        flex: 1, padding: "60px 36px", display: "flex",
        flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center",
      }}>
        <div style={{ fontSize: 11, letterSpacing: "0.42em", color: "#F39A1F", fontWeight: 700, marginBottom: 30 }}>
          ★ FAITHARAMA ★
        </div>
        <div style={{
          fontFamily: "Anton, Impact, sans-serif",
          fontSize: 88, lineHeight: 1, color: "#0F172A", letterSpacing: "0.02em",
        }}>
          {(fb.title || "").split("·")[0].trim()}
        </div>
        <div style={{ width: 80, height: 4, background: "#F39A1F", margin: "28px 0" }}/>
        <div style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 26, lineHeight: 1.2, color: "#0F172A",
          fontWeight: 700, textTransform: "uppercase", maxWidth: 420,
        }}>
          {(fb.title || "").split("·").slice(1).join("·").trim() || fb.title}
        </div>
        {fb.subtitle && (
          <div style={{
            marginTop: 22, fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontStyle: "italic", fontSize: 18, color: "#0F172A", maxWidth: 420,
          }}>{fb.subtitle}</div>
        )}
      </div>
    </div>
  );
}

export default function MasterBookPrint() {
  const [books, setBooks] = useState(null);
  const [imagesReady, setImagesReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await api.get("/flipbooks");
        const items = list.data?.flipbooks || [];
        // Sort by "DAY N" number extracted from title — ascending
        const dayNum = (t) => {
          const m = (t || "").match(/DAY\s+(\d+)/i);
          return m ? parseInt(m[1], 10) : 9999;
        };
        items.sort((a, b) => dayNum(a.title) - dayNum(b.title));
        const slugs = items.map(f => f.slug);
        // Fetch full bodies in parallel — each call returns full pages array
        const fulls = await Promise.all(
          slugs.map(s => api.get(`/flipbooks/${s}`).then(r => r.data).catch(() => null))
        );
        setBooks(fulls.filter(Boolean));
      } catch (e) {
        setBooks([]);
      }
    })();
  }, []);

  // Wait for every <img> on the master page to settle, then signal ready
  useEffect(() => {
    if (!books) return;
    let cancelled = false;
    (async () => {
      await new Promise(r => setTimeout(r, 200));
      const imgs = Array.from(document.images);
      await Promise.all(
        imgs.map(img =>
          img.complete ? Promise.resolve() :
          new Promise(res => {
            img.addEventListener("load", res, { once: true });
            img.addEventListener("error", res, { once: true });
          })
        )
      );
      await new Promise(r => setTimeout(r, 800));
      if (!cancelled) setImagesReady(true);
    })();
    return () => { cancelled = true; };
  }, [books]);

  if (!books) return <div style={{ padding: 40 }}>Loading your book…</div>;
  if (books.length === 0) return <div style={{ padding: 40 }}>No flipbooks yet.</div>;

  return (
    <div
      data-testid="master-book-print"
      data-print-ready={imagesReady ? "true" : "false"}
      style={{ background: "#FFFFFF", minHeight: "100vh" }}
    >
      <style>{`
        @page { size: 5.83in 8.27in; margin: 0; }
        @media print {
          body { margin: 0; padding: 0; background: #FFF; }
          [data-testid="master-book-print"] > div { page-break-after: always; break-after: page; }
        }
        body { margin: 0; padding: 0; }
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;700&family=Anton&display=swap');
      `}</style>

      {/* Master cover */}
      <div style={{ display: "flex", justifyContent: "center", margin: 0 }}>
        <MasterCover count={books.length}/>
      </div>

      {/* Each flipbook in order */}
      {books.map((fb) => (
        <div key={fb.slug} style={{ display: "contents" }}>
          {/* Day separator */}
          <div style={{ display: "flex", justifyContent: "center", margin: 0 }}>
            <DaySeparator fb={fb}/>
          </div>
          {/* Pages of this flipbook (skip the original flipbook cover — we already have a big day separator) */}
          {(fb.pages || [])
            .filter(p => p.kind !== "cover")
            .map((p, i) => {
              const Comp = RENDER[p.kind] || TextP;
              return (
                <div key={`${fb.slug}-${i}`} style={{ display: "flex", justifyContent: "center", margin: 0 }}>
                  <Comp page={p} fb={fb}/>
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
}
